import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Bottom sheet.
 *
 * Accessibility obligations from design/ACCESSIBILITY_CHECKLIST.md:
 * "Dialog focus is trapped and returns to the invoking control."
 * Escape closes; the backdrop click closes; the title names the dialog.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Sheet({
  open,
  onClose,
  title,
  children,
  describedBy,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  describedBy?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const pointerDownOnBackdrop = useRef(false);
  const titleId = useId();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const target = panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
    target?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  /**
   * Portal the dialog out of the page flow.
   *
   * Rendered inline, the backdrop's `inset: 0` resolves against whichever
   * ancestor happens to establish a containing block, which sized it to the
   * scrolling content rather than the screen. Portalling to the device screen
   * (or the body on a real phone) makes the overlay cover exactly the surface
   * it belongs to.
   */
  return createPortal(
    <div
      className="tm-sheet-backdrop"
      /**
       * Dismiss only when the gesture both STARTED and ENDED on the backdrop.
       *
       * A plain onClick check is unsafe: if the control under the pointer
       * re-renders between pointerdown and click — which happens whenever a
       * button inside the sheet swaps the sheet's content — the browser
       * retargets the click to the nearest surviving ancestor, the backdrop,
       * and the sheet dismisses itself under the user.
       */
      onPointerDown={(e) => {
        pointerDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pointerDownOnBackdrop.current) onClose();
        pointerDownOnBackdrop.current = false;
      }}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={panelRef}
        className="tm-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        tabIndex={-1}
      >
        <div className="tm-sheet__grabber" aria-hidden="true" />
        <h2 className="tm-sheet__title" id={titleId}>
          {title}
        </h2>
        {children}
      </div>
    </div>,
    overlayRoot(),
  );
}

/** The device screen when framed on desktop, otherwise the document body. */
export function overlayRoot(): HTMLElement {
  return document.querySelector<HTMLElement>('.tm-stage__screen') ?? document.body;
}

/** Confirmation sheet for destructive or consequential actions. */
export function ConfirmSheet({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  tone = 'primary',
  busy = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
}) {
  const bodyId = useId();
  return (
    <Sheet open={open} onClose={onClose} title={title} describedBy={bodyId}>
      <div id={bodyId} className="tm-muted" style={{ marginBlockEnd: 16 }}>
        {body}
      </div>
      <div className="tm-stack tm-stack--tight">
        <button
          type="button"
          className={`tm-btn tm-btn--block${tone === 'danger' ? ' tm-btn--danger' : ''}`}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
        <button
          type="button"
          className="tm-btn tm-btn--secondary tm-btn--block"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </Sheet>
  );
}
